import { DeviceCheckNotes, FieldProse, NumberedSection, obj, PanelCard, Placeholder } from './DossierPrimitives';
import { EuGsprSection } from './EuTdRenderer';

/**
 * EU IVDR (Reg 2017/746) Annex II Technical Documentation renderer — §1–§6.
 *
 * Parity target: backend/services/dossier/eu_ivdr_td_renderer (`td_sections_html`)
 * — the IVDR PDF source of truth. IVD-specific vs MDR: §1 carries analyte /
 * specimen / test-principle / scientific-validity; §6 carries analytical +
 * clinical performance (the Performance Evaluation Report). The §4 GSPR table is
 * identical to MDR, so it reuses the shared `EuGsprSection`.
 *
 * Field skipping + placeholder discipline mirror the MDR renderer: a `null`
 * field is omitted (the Python `_field` returns "" for None); a present value
 * (incl. inline `__PLACEHOLDER__:hint` markers) renders via `FieldProse`.
 */

interface SubmissionHeader {
  product_name?: string | null;
  company?: string | null;
  ec_rep?: string | null;
  notified_body?: string | null;
  prrc?: string | null;
  srn?: string | null;
}

export function IvdrTdRenderer({
  dossier,
  submissionHeader,
  hideCover = false,
}: {
  dossier: Record<string, unknown>;
  submissionHeader?: SubmissionHeader;
  hideCover?: boolean;
}) {
  return (
    <div className="space-y-4">
      {!hideCover && <IvdrCoverBlock dossier={dossier} submissionHeader={submissionHeader} />}
      <DeviceCheckNotes dossier={dossier} />
      <Section1 dossier={dossier} />
      <Section2 dossier={dossier} />
      <Section3 dossier={dossier} />
      <EuGsprSection dossier={dossier} />
      <Section5 dossier={dossier} />
      <Section6 dossier={dossier} />
    </div>
  );
}

// ── Cover block ──────────────────────────────────────────────────────────────

function IvdrCoverBlock({
  dossier,
  submissionHeader,
}: {
  dossier: Record<string, unknown>;
  submissionHeader?: SubmissionHeader;
}) {
  const s1 = obj(dossier, 's1_device_description');
  const deviceClass = dossier['device_class'];
  const basicUdi = dossier['basic_udi_di'] ?? s1['b_basic_udi_di'];

  return (
    <PanelCard>
      <div className="text-xs tracking-[0.16em] uppercase text-muted font-bold mb-1.5">
        EU IVDR Technical Documentation
      </div>
      <p className="text-[11px] text-muted m-0 mb-4">
        Regulation (EU) 2017/746 — Annex II
      </p>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <CoverCell
          label="Device trade name"
          value={submissionHeader?.product_name}
          fallbackHint="Device trade name"
        />
        <CoverCell label="Device class (EU IVDR)" value={deviceClass} />
        <CoverCell
          label="Basic UDI-DI"
          value={basicUdi}
          fallbackHint="Assigned upon registration in EUDAMED"
        />
        <CoverCell
          label="Manufacturer / legal manufacturer"
          value={submissionHeader?.company}
          fallbackHint="Manufacturer name and address"
        />
        <CoverCell
          label="EU Authorised Representative (EC REP)"
          value={submissionHeader?.ec_rep}
          fallbackHint="EC REP name and address"
        />
        <CoverCell
          label="Single Registration Number (SRN)"
          value={submissionHeader?.srn}
          fallbackHint="Manufacturer SRN (assigned in EUDAMED)"
        />
        <CoverCell
          label="PRRC (Art. 15)"
          value={submissionHeader?.prrc}
          fallbackHint="PRRC name"
        />
        <CoverCell
          label="Notified Body / EU reference laboratory"
          value={submissionHeader?.notified_body}
          fallbackHint="NB / EU reference laboratory (if applicable)"
        />
      </div>
    </PanelCard>
  );
}

function CoverCell({
  label,
  value,
  fallbackHint,
}: {
  label: string;
  value?: unknown;
  fallbackHint?: string;
}) {
  const s = value == null ? '' : String(value);
  const isPlaceholder = s === '__PLACEHOLDER__' || s.startsWith('__PLACEHOLDER__:');
  const hint = isPlaceholder ? s.replace(/^__PLACEHOLDER__:?/, '').trim() || null : null;
  return (
    <div className="bg-[#f8f9fa] rounded-md py-2.5 px-3">
      <div className="text-[10px] uppercase tracking-[0.12em] font-bold text-muted mb-0.5">
        {label}
      </div>
      <div className="text-[13px] text-navy">
        {isPlaceholder ? (
          <Placeholder hint={hint} />
        ) : s ? (
          s
        ) : (
          <Placeholder hint={fallbackHint || label} />
        )}
      </div>
    </div>
  );
}

// ── Prose section (skip null fields; render present ones) ─────────────────────

function ProseSection({
  num,
  title,
  fields,
}: {
  num: number;
  title: string;
  fields: [unknown, string][];
}) {
  const present = fields.filter(([val]) => val != null);
  return (
    <NumberedSection prefix="Section" num={num} title={title}>
      {present.length === 0 ? (
        <p className="text-[13.5px] text-muted italic m-0">No content provided for this section.</p>
      ) : (
        present.map(([val, label], i) => (
          <FieldProse key={i} label={`${label}:`} body={val} fallbackHint={label} />
        ))
      )}
    </NumberedSection>
  );
}

// §1 Device Description and Specification (IVD)
function Section1({ dossier }: { dossier: Record<string, unknown> }) {
  const s1 = obj(dossier, 's1_device_description');
  return (
    <ProseSection
      num={1}
      title="Device Description and Specification"
      fields={[
        [s1['a_name_and_general_description'], 'Name and general description'],
        [s1['a_intended_purpose'], 'Intended purpose'],
        [s1['a_what_is_detected_or_measured'], 'What is detected / measured (analyte)'],
        [s1['a_specimen_type'], 'Specimen type'],
        [s1['a_test_principle_methodology'], 'Test principle / methodology'],
        [s1['a_intended_user'], 'Intended user'],
        [s1['a_qualitative_quantitative'], 'Qualitative / quantitative'],
        [s1['b_basic_udi_di'], 'Basic UDI-DI'],
        [s1['c_intended_patient_population'], 'Intended patient population'],
        [s1['c_clinical_indication'], 'Clinical indication'],
        [s1['d_scientific_validity_basis'], 'Scientific validity basis'],
        [s1['e_rationale_qualification_as_ivd'], 'Rationale for qualification as an IVD'],
        [s1['f_risk_class'], 'Risk class'],
        [s1['f_classification_rule'], 'Classification rule'],
        [s1['g_novel_features'], 'Novel features'],
        [s1['h_accessories_calibrators_controls'], 'Accessories / calibrators / controls'],
        [s1['i_variants_and_configurations'], 'Variants and configurations'],
        [s1['j_key_components_and_materials'], 'Key components and materials'],
        [s1['previous_generations'], 'Previous generations'],
        [s1['similar_devices_eu_intl_market'], 'Similar devices on the EU / international market'],
      ]}
    />
  );
}

// §2 Information Supplied by the Manufacturer
function Section2({ dossier }: { dossier: Record<string, unknown> }) {
  const s2 = obj(dossier, 's2_information_supplied');
  return (
    <ProseSection
      num={2}
      title="Information Supplied by the Manufacturer"
      fields={[
        [s2['labels_and_packaging'], 'Labels and packaging'],
        [s2['instructions_for_use'], 'Instructions for use'],
        [s2['languages'], 'Languages'],
      ]}
    />
  );
}

// §3 Design and Manufacturing Information
function Section3({ dossier }: { dossier: Record<string, unknown> }) {
  const s3 = obj(dossier, 's3_design_and_manufacturing');
  return (
    <ProseSection
      num={3}
      title="Design and Manufacturing Information"
      fields={[
        [s3['a_design_stages'], 'Design stages'],
        [s3['b_manufacturing_processes_validation'], 'Manufacturing processes and validation'],
        [s3['c_all_sites'], 'All sites'],
      ]}
    />
  );
}

// §5 Benefit-Risk Analysis and Risk Management
function Section5({ dossier }: { dossier: Record<string, unknown> }) {
  const s5 = obj(dossier, 's5_benefit_risk_and_risk_management');
  return (
    <ProseSection
      num={5}
      title="Benefit-Risk Analysis and Risk Management"
      fields={[
        [s5['a_benefit_risk_analysis'], 'Benefit-risk analysis'],
        [s5['b_risk_management_rm_plan'], 'Risk management plan'],
        [s5['b_risk_management_hazard_analysis_fmea'], 'Hazard analysis / FMEA'],
        [s5['b_risk_management_rm_report'], 'Risk management report'],
      ]}
    />
  );
}

// §6 Verification and Validation Evidence (analytical + clinical performance)
function Section6({ dossier }: { dossier: Record<string, unknown> }) {
  const s6 = obj(dossier, 's6_verification_and_validation');
  const ap = obj(s6, 's6_1_analytical_performance');
  const cp = obj(s6, 's6_2_clinical_performance');
  return (
    <ProseSection
      num={6}
      title="Verification and Validation Evidence"
      fields={[
        [ap['trueness'], 'Analytical — trueness'],
        [ap['precision_repeatability_reproducibility'], 'Analytical — precision (repeatability/reproducibility)'],
        [ap['analytical_sensitivity'], 'Analytical — sensitivity (LoD/LoQ)'],
        [ap['analytical_specificity'], 'Analytical — specificity / interferences'],
        [ap['measuring_range_linearity'], 'Analytical — measuring range / linearity'],
        [ap['cut_off_determination'], 'Analytical — cut-off determination'],
        [ap['metrological_traceability_of_calibrators'], 'Metrological traceability of calibrators'],
        [ap['specimen_stability'], 'Specimen stability'],
        [cp['scientific_validity_report'], 'Scientific validity report'],
        [cp['clinical_performance'], 'Clinical performance'],
        [cp['performance_evaluation_report'], 'Performance Evaluation Report (PER)'],
        [cp['performance_studies'], 'Performance studies'],
        [cp['pmpf_plan_report'], 'PMPF plan / report'],
        [s6['stability_shelf_life'], 'Stability / shelf life'],
        [s6['software_verification_validation'], 'Software verification and validation'],
        [s6['additional_specific_cases'], 'Additional specific cases'],
      ]}
    />
  );
}
